import { CaretDownFilled, CaretUpFilled, SearchOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, Spin } from 'antd';
import Search from 'antd/lib/input/Search';
import classNames from 'classnames';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { getParams, setParams } from '../../../branding/utils/urlManipulation';
import { Translate, getTranslatedString } from '../../../services/translationService/components/Translate';
import { PageContainer, PageContent, PaginationController } from '../../common';
import LearningList from '../../common/learningList';
import './styles.less';
import { useSearchLazyQuery } from '../../../gql/hooks/queries/useSearchLazyQuery';
import { useTranslations } from '../../../hooks/translations';
import { BreadcrumbContext } from '../../../breadcrumbs/BreadcrumbContext';

const filterKeys = {
    resources: 'resources',
    courses: 'courses',
    curriculum: 'curricula',
};

const sortKeys = {
    closest: '_score', 
    recent: 'createdAt',
    alpha: 'name',
    antialpha: 'name',
};

const typeKeys = {
    eLearning: 'elearning',
    ilt: 'ilt',
    allLearning: 'allLearning',
};

function canUseLearningType( filter ) {
    return filter.length == 0 || filter.includes( filterKeys.courses );
}

export const SearchLibrary = ( { pageKey } ) => {

    const initialUrlParams = getParams();

    const { locale } = useTranslations();

    const { leaveCrumb } = useContext( BreadcrumbContext );    
    
    useEffect( () => {leaveCrumb( pageKey );}, [] );
    
    const [ searched, setSearched ] = useState( false );
    const [ searchStr, setSearchStr ] = useState( initialUrlParams.search ?? '' );
    const [ pageData, setPageData ] = useState( initialUrlParams.page ? { page: parseInt( initialUrlParams.page ) } : { page: 1 } );
    // these store the keys, not the values
    const [ sortInfo, setSortInfo ] = useState( initialUrlParams.sort ?? 'closest' );
    const [ filterInfo, setFilterInfo ] = useState( initialUrlParams.filter?.split( ',' ).filter( i => i !== '' ) ?? [] ); 
    const [ learningType, setLearningType ] = useState( initialUrlParams.courseIsIlt == 'true' ? typeKeys.ilt : initialUrlParams.courseIsIlt == 'false' ? typeKeys.eLearning : typeKeys.allLearning );
    const [ alphaDesc, setAlphaDesc ] = useState( initialUrlParams.sort == 'antialpha' ); 

    const [ searchQuery, { data, loading, error, pageData: searchPageData } ] = useSearchLazyQuery( {}, false, false );
    const query = ( search, page, sort, filter, courseIsIlt ) => {
        searchQuery( {
            types: filter.length == 0 ? [ filterKeys.resources, filterKeys.courses, filterKeys.curriculum ] : filter.map( f => filterKeys[ f ] ),
            query: search,
            page: page ?? 1,
            courseIsIlt,
            limit: 10, 
            sort: sortKeys[ sort ],
            order: sort === 'alpha' ? 'asc' : 'desc', // awkward, but alpha is 'asc', antialpha is 'desc', and closest and recent are both 'desc'
        } );
        setSearched( true );
    };

    const [ paginationController, setPaginationController ] = useState();

    function fetch( { search, pageNumber, sort, filter, type = learningType } ) {

        const courseIsIlt = canUseLearningType( filter ?? filterInfo ) ? ( type == typeKeys.eLearning ? false : type == typeKeys.ilt ? true : undefined ) : undefined;
        const params = {
            search: search ?? searchStr,
            page: pageNumber ?? pageData.page,
            sort: sort ?? sortInfo,
            filter: ( filter ?? filterInfo ).join( ',' ),
            courseIsIlt,
        };

        setParams( params );
        
        query( params.search, params.page, params.sort, filter ?? filterInfo, courseIsIlt );
    }

    useEffect( () => {
        fetch( { search: searchStr } );
    }, [ locale ] );

    function onSearch( search ) {

        paginationController?.setActivePage( 0 );
        setSearchStr( search );
        fetch( { search, pageNumber: 1 } );
    }

    function onLearningTypeChange( type ) {
        setLearningType( type );
        if ( learningType !== type ) {
            paginationController?.setActivePage( 0 );
            fetch( { type, pageNumber: 1 } );
        }
    }

    function onPageChange( page ) {

        setPageData( { ...pageData, page } );
        fetch( { pageNumber: page, search: searchStr } );
    }

    function onFilterChange( filter ) {

        paginationController?.setActivePage( 0 );
        setFilterInfo( filter );
        fetch( { filter, search: searchStr, pageNumber: 1 } );
    }

    function toggleFilterOption( opt ) {
        if ( filterInfo.includes( opt ) ) {
            onFilterChange( filterInfo.filter( f => f !== opt ) );
        } else {
            onFilterChange( [ ...filterInfo, opt ] );
        }
    }

    function onSortChange( sort ) {

        paginationController?.setActivePage( 0 );
        setSortInfo( sort );
        fetch( { sort, search: searchStr, pageNumber: 1 } );
    }

    const filterItems = useMemo( () => [
        {
            getLabel: () => <Translate.Text value='control.all' />,
            key: 'all',
            isSelected: () => filterInfo.length === 0,
            onSelect: () => onFilterChange( [] ),
        },
        {
            getLabel: () => <Translate.Text value='control.resources'/>,
            key: 'resources',
            isSelected: () => filterInfo.includes( 'resources' ),
            onSelect: () => toggleFilterOption( 'resources' )
        },
        {
            getLabel: () => <Translate.Text value='control.courses'/>,
            key: 'courses',
            isSelected: () => filterInfo.includes( 'courses' ),
            onSelect: () => toggleFilterOption( 'courses' )
        },
        {
            getLabel: () => <Translate.Text value='control.curriculum'/>,
            key: 'curriculum',
            isSelected: () => filterInfo.includes( 'curriculum' ),
            onSelect: () => toggleFilterOption( 'curriculum' )
        }
    ], [ locale, filterInfo ] );

    const sortItems = useMemo( () => [
        {
            getLabel: () => <Translate.Text value='control.closestMatch' />,
            key: 'closest',
            isSelected: () => sortInfo === 'closest',
            onSelect: () => onSortChange( 'closest' )
        },
        {
            getLabel: () => <Translate.Text value='control.mostRecent' />,
            key: 'recent',
            isSelected: () => sortInfo === 'recent',
            onSelect: () => onSortChange( 'recent' )
        },
        {
            getLabel: () => !alphaDesc ? <><Translate.Text value="control.alphabetical" /><CaretDownFilled /></> : <><Translate.Text value="control.antiAlphabetical" /><CaretUpFilled /></>,
            key: 'alpha',
            isSelected: () => [ 'alpha', 'antialpha' ].includes( sortInfo ),
            onSelect: () => {
                if ( [ 'alpha', 'antialpha' ].includes( sortInfo ) ) {
                    // this button was selected prior so flip it
                    onSortChange( alphaDesc ? 'alpha' : 'antialpha' );
                    setAlphaDesc( !alphaDesc );
                } else {
                    // this button wasnt selected prior
                    onSortChange( alphaDesc ? 'antialpha' : 'alpha' );
                }
            }
        }
    ], [ locale, sortInfo ] );

    useEffect( () => {
        if ( searchPageData.totalResults !== undefined && !loading ) {
            setPageData( searchPageData );
        }
    }, [ searchPageData, loading ] );

    const getSearchPlaceholder = useMemo( () => ( () => getTranslatedString( 'page.searchLibrary.placeholder' ) ), [ locale, getTranslatedString ] );

    return (
        <PageContent className="page-search-library">
            
            { /* Banner */ }
            <PageContainer className="search-library-banner-container">

            <div className='page-banner info'>
                <div className='title'><h2><Translate.Text value="header.nav.searchLibrary" /></h2></div>
                <Search className='search bordered-tertiary' 
                    defaultValue={ searchStr } 
                    onSearch={ onSearch } 
                    enterButton={ <button className='primary' ><SearchOutlined /></button> } 
                    placeholder={ getSearchPlaceholder() } />
            </div>

            <div className='search-content'>
                <div className='controls'>
                    <div className='filter-controls non-compact'>
                        <div className='primary-filters'>
                            {
                                filterItems.map( filter => 
                                    <Button key={ filter.key } onClick={ () => filter.onSelect() } className={ classNames( { active: filter.isSelected() }, 'control' ) }>
                                        { filter.getLabel() }
                                    </Button> )
                            }
                            <Button onClick={ () => onFilterChange( [] ) } className='reset-filters'>
                                <Translate.Text value="control.reset" />
                            </Button>
                        </div>
                    </div>
                    <div className='filter-controls compact'>
                        <div className='primary-filters'>
                            <Dropdown 
                                trigger={ [ 'click' ] }
                                overlay={
                                    <Menu>
                                    {
                                        filterItems.map( f => 
                                            <Menu.Item key={ f.key } 
                                                className={ classNames( 'selectable', { selected: ( filterInfo.length == 0 && f.key == 'all' ) || filterInfo.includes( f.key ) } ) }
                                                onClick={ () => f.onSelect() } >
                                                { f.getLabel() }
                                            </Menu.Item> )
                                    }
                                    </Menu>
                                }
                            >
                                <Button className={ classNames( 'active', 'control' ) } >
                                    <span className='button-text'>
                                        {
                                            filterInfo.length === 0 ? filterItems.find( f => f.key == 'all' ).getLabel() :
                                                filterInfo.length === 1 ? filterItems.find( f => f.key == filterInfo[ 0 ] ).getLabel() :
                                                ( <>
                                                    { filterItems.find( f => f.key == filterInfo[ 0 ] ).getLabel() }
                                                    <span>{ `+${ filterInfo.length - 1 }` }</span>
                                                </> )
                                        }
                                        <CaretDownFilled />
                                    </span>
                                </Button>
                                    
                            </Dropdown>
                        </div>
                    </div>

                    
                    { canUseLearningType( filterInfo ) && 
                        <div className='type-controls'>
                            <Dropdown 
                                trigger={ [ 'click' ] }
                                overlay={
                                    <Menu>
                                        <Menu.Item key={ typeKeys.allLearning } 
                                            className={ classNames( 'selectable', { selected: ( learningType == typeKeys.allLearning ) } ) }
                                            onClick={ () => onLearningTypeChange( typeKeys.allLearning ) } >
                                            <Translate.Text value='button.filter.learning.any'/>
                                        </Menu.Item>
                                        <Menu.Item key={ typeKeys.eLearning } 
                                            className={ classNames( 'selectable', { selected: ( learningType == typeKeys.eLearning ) } ) }
                                            onClick={ () => onLearningTypeChange( typeKeys.eLearning ) } >
                                            <Translate.Text value='button.filter.elearning'/>
                                        </Menu.Item>
                                        <Menu.Item key={ typeKeys.ilt } 
                                            className={ classNames( 'selectable', { selected: ( learningType == typeKeys.ilt ) } ) }
                                            onClick={ () => onLearningTypeChange( typeKeys.ilt ) } >
                                            <Translate.Text value='button.filter.ilt'/>
                                        </Menu.Item>
                                    </Menu>
                                }
                            >
                                <Button shape='round' className={ classNames( 'active', 'control' ) } >
                                    <span className='button-text'>
                                        { learningType == typeKeys.eLearning ? <Translate.Text value='button.filter.elearning'/> :
                                            learningType == typeKeys.ilt ? <Translate.Text value='button.filter.ilt'/> : 
                                            learningType == typeKeys.allLearning ? <Translate.Text value='button.filter.learning.any'/> : <></> }
                                        <CaretDownFilled />
                                    </span>
                                </Button>
                                    
                            </Dropdown>
                        </div> 
                    }
                    <div className='sort-controls'>
                        { sortItems.map( sort => 
                                <Button key={ sort.key } onClick={ () => sort.onSelect() } className={ classNames( { active: sort.isSelected() }, 'control' ) } >
                                    { sort.getLabel() }
                                </Button> ) }
                    </div>

                </div>
                <div className='search-results'>
                    {
                        loading || data == null ? <Spin /> :    // loading
                            data?.length === 0 && error === undefined && !searched ? <div className='empty-page'><Translate.Text className='title' value='page.searchLibrary.startPage' /></div> : // nothing has been searched 
                            data?.length === 0 && ( searched || error ) ? <div className='empty-page no-border'><Translate.Text className='title' value='page.searchLibrary.empty' /></div> : // nothing is returned from search
                            <LearningList
                                displayType='search'
                                data={ data }
                                isLoading={ loading }
                                />
                    }
                </div>
            </div>

            <div className={ classNames( 'learning-list-pagination-controller', { loading } ) }>
                { pageData?.totalResults > 0 &&
                    <PaginationController
                        pageSize={ pageData.limit }
                        initialPage={ pageData.page - 1 }
                        itemCount={ pageData.totalResults }
                        onChange={ ( index ) => onPageChange( index + 1 ) }
                        getController={ setPaginationController }
                    />
                }
                        
                </div>
                
            </PageContainer>
       </PageContent>
    );

};